  import React, { useEffect, useRef, useState } from "react";
  import Marquee from "react-fast-marquee";
  import "./EventCard.css";

  // EventCard component to handle image border sizing
  const EventCard = ({ card, index, isCenter }) => {
    const imageRef = useRef(null);
    const wrapperRef = useRef(null);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0, left: 0, top: 0 });

    useEffect(() => {
      const updateImageSize = () => {
        if (imageRef.current && wrapperRef.current) {
          const img = imageRef.current;
          const wrapper = wrapperRef.current;
          
          // Wait for image to load if not already loaded
          if (!img.complete || img.naturalWidth === 0) {
            return;
          }
          
          const containerRect = wrapper.getBoundingClientRect();
          
          // Get actual rendered image dimensions with object-fit: contain
          // IMPORTANT: Never scale beyond natural image dimensions (1:1 or smaller)
          const containerAspect = containerRect.width / containerRect.height;
          const imgAspect = img.naturalWidth / img.naturalHeight;
          
          let renderedWidth, renderedHeight, offsetLeft, offsetTop;
          
          // Calculate size that fits container but never exceeds natural dimensions
          if (imgAspect > containerAspect) {
            // Image is wider - width fills container (but cap at natural width)
            renderedWidth = Math.min(containerRect.width, img.naturalWidth);
            renderedHeight = renderedWidth / imgAspect;
            // If height exceeds container, scale down proportionally
            if (renderedHeight > containerRect.height) {
              renderedHeight = containerRect.height;
              renderedWidth = renderedHeight * imgAspect;
            }
            offsetLeft = (containerRect.width - renderedWidth) / 2;
            offsetTop = (containerRect.height - renderedHeight) / 2;
          } else {
            // Image is taller - height fills container (but cap at natural height)
            renderedHeight = Math.min(containerRect.height, img.naturalHeight);
            renderedWidth = renderedHeight * imgAspect;
            // If width exceeds container, scale down proportionally
            if (renderedWidth > containerRect.width) {
              renderedWidth = containerRect.width;
              renderedHeight = renderedWidth / imgAspect;
            }
            offsetLeft = (containerRect.width - renderedWidth) / 2;
            offsetTop = (containerRect.height - renderedHeight) / 2;
          }
          
          setImageSize({
            width: renderedWidth,
            height: renderedHeight,
            left: offsetLeft,
            top: offsetTop
          });
        }
      };

      const img = imageRef.current;
      if (img) {
        if (img.complete && img.naturalWidth > 0) {
          updateImageSize();
        } else {
          img.addEventListener('load', updateImageSize, { once: true });
        }
      }

      window.addEventListener('resize', updateImageSize);
      
      return () => {
        if (img) {
          img.removeEventListener('load', updateImageSize);
        }
        window.removeEventListener('resize', updateImageSize);
      };
    }, [card.image]);

    return (
      <div 
        className="event-card-wrapper" 
        data-card-id={card.id}
      >
        <div
          className={`event-card relative ${isCenter ? 'is-center' : ''}`}
          style={{
            width: "400px",
            height: "550px",
            borderRadius: "16px",
            overflow: "hidden",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div ref={wrapperRef} className="image-wrapper">
            <img
              ref={imageRef}
              src={card.image}
              alt={`Event ${card.id}`}
              loading={index < 3 ? "eager" : "lazy"}
              style={{
                // Prevent upscaling - always 1:1 or smaller
                maxWidth: "100%",
                maxHeight: "100%",
                width: "auto",
                height: "auto",
                // Prevent stretching
                objectFit: "contain",
                objectPosition: "center center",
                display: "block",
                // Sharp rendering - prevent browser smoothing
                imageRendering: "crisp-edges",
                // Prevent subpixel rendering blur
                transform: "translateZ(0)",
                backfaceVisibility: "hidden",
                // Prevent scaling from parent
                willChange: "auto",
              }}
            />
            {imageSize.width > 0 && imageSize.height > 0 && (
              <div 
                className="card-animated-border"
                style={{
                  width: `${imageSize.width}px`,
                  height: `${imageSize.height}px`,
                  left: `${imageSize.left}px`,
                  top: `${imageSize.top}px`,
                }}
              ></div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const Events = () => {
    const cards = [
      { id: 1, image: "/asset/c1.png" },
      { id: 2, image: "/asset/c2.png" },
      { id: 3, image: "/asset/c3.png" },
      { id: 4, image: "/asset/c4.png" },
      { id: 5, image: "/asset/c5.png" },
      { id: 6, image: "/asset/c6.png" },
      { id: 7, image: "/asset/c7.png" },
    ];

    const containerRef = useRef(null);
    const [centerCardId, setCenterCardId] = useState(null);

    useEffect(() => {
      const updateCenterCard = () => {
        if (!containerRef.current) return;

        const viewportCenterX = window.innerWidth / 2;
        const cardElements = containerRef.current.querySelectorAll('.event-card');
        
        let closestCard = null;
        let minDistance = Infinity;

        cardElements.forEach((cardElement) => {
          const cardRect = cardElement.getBoundingClientRect();
          const cardCenterX = cardRect.left + cardRect.width / 2;
          const distance = Math.abs(cardCenterX - viewportCenterX);

          if (distance < minDistance) {
            minDistance = distance;
            closestCard = cardElement;
          }
        });

        if (closestCard) {
          const cardId = parseInt(closestCard.closest('.event-card-wrapper').dataset.cardId);
          setCenterCardId(cardId);
        }
      };

      // Initial update
      const initTimeout = setTimeout(updateCenterCard, 100);

      // Update on resize
      window.addEventListener('resize', updateCenterCard, { passive: true });

      // Use requestAnimationFrame to continuously check center card position
      let rafId = null;
      const updateLoop = () => {
        updateCenterCard();
        rafId = requestAnimationFrame(updateLoop);
      };
      rafId = requestAnimationFrame(updateLoop);

      return () => {
        clearTimeout(initTimeout);
        window.removeEventListener('resize', updateCenterCard);
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
      };
    }, []);

    return (
      <section className="relative w-screen sm:pt-10 sm:pb-1  md:pt-20 pb-8" style={{ overflow: 'visible' }}>
  <div className="hidden lg:block absolute bottom-[310px] left-1/2 transform -translate-x-full -translate-y-1/2 w-[960px] h-[2px] z-0 pointer-events-none">
              {/* <div className="w-full h-full" style={{ background: 'rgba(255, 255, 255, 0.1)', position: 'relative', overflow: 'hidden' }}>
                <div
                  className="absolute top-0 right-0 h-full"
                  style={{
                    width: '100px',
                    background: 'linear-gradient(90deg, rgba(244, 76, 29, 0) 0%, rgba(244, 76, 29, 1) 50%, rgba(244, 76, 29, 0) 100%)',
                    animation: 'slide-across-left 4s linear infinite'
                  }}
                ></div>
              </div> */}
            </div>

            {/* Animated Line Connecting Card 4 forward to Card 7 */}
            <div className="hidden lg:block absolute bottom-[300px] left-1/2 transform -translate-y-1/2 w-[960px] h-[2px] z-0 pointer-events-none">
              {/* <div className="w-full h-full" style={{ background: 'rgba(255, 255, 255, 0.1)', position: 'relative', overflow: 'hidden' }}>
                <div
                  className="absolute top-0 left-0 h-full"
                  style={{
                    width: '100px',
                    background: 'linear-gradient(90deg, rgba(244, 76, 29, 0) 0%, rgba(244, 76, 29, 1) 50%, rgba(244, 76, 29, 0) 100%)',
                    animation: 'slide-across 3s linear infinite'
                  }}
                ></div>
              </div> */}
            </div>
        {/* ⭐ TOP TEXT */}
        <div className="text-center mb-4 px-4   md:px-6 lg:px-8 relative z-20">
          <h2 className="text-white text-4xl font-semibold">
            Discover and Connect at{" "}
            <span className="text-[#FF4D00]">Car Events</span>
          </h2>
          <p className="text-gray-200 mt-3 sm:-mb-12 max-w-2xl mx-auto">
            Find, join, and host car events with ease. Car Hangout connects
            enthusiasts to the gatherings and experiences that fuel your passion.
          </p>
        </div>

        {/* 🔥 LEFT SIDE TOP LINE */}
        {/* <div className="hidden lg:flex absolute top-[160px] left-[340px] -translate-x-1/2 w-[900px] h-[150px] justify-center items-center z-0 pointer-events-none left-top-line-adjust">

              <svg
    viewBox="0 0 900 150"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-full h-full object-contain"
    style={{ transform: 'scaleX(1)', transformOrigin: '50% 50%' }}
  >
    <defs> 
      {/* Static faint base line */}
     {/* <linearGradient
        id="baseLine4"
        x1="27"
        y1="75"
        x2="900"
        y2="75"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="white" stopOpacity="0.08" />
        <stop offset="1" stopColor="white" stopOpacity="0.01" />
      </linearGradient> */}

      {/* Moving gradient - bright front, faded tail */}
      {/* <linearGradient id="glowTrail4" x1="0" y1="75" x2="250" y2="75" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="rgba(244, 76, 29, 0)" />
        <stop offset="40%" stopColor="rgba(244, 76, 29, 0.6)" />
        <stop offset="50%" stopColor="rgba(244, 76, 29, 1)" />
        <stop offset="60%" stopColor="rgba(244, 76, 29, 0.6)" />
        <stop offset="100%" stopColor="rgba(244, 76, 29, 0)" />
        <animateTransform
          attributeName="gradientTransform"
          type="translate"
          values="650 0; -900 0"
          dur="3s"
          repeatCount="indefinite"
        />
      </linearGradient> */}

      {/* Soft orange glow */}
      {/* <filter id="orangeGlow4" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="1" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="blur" />
          <feMergeNode in="blur" />
        </feMerge>
      </filter>
    </defs> */}

    {/* Base path */}
    {/* <path
      d="M900 149L401 149C389.954 149 381 142.046 381 131L381 21C381 9.95427 372.046 0.999968 361 0.999967L-9 0.999938"
      stroke="url(#baseLine4)"
      strokeWidth="2"
      fill="none"
    /> */}

    {/* Animated glowing line with moving gradient */}
     {/* <path
      d="M900 149L401 149C389.954 149 381 142.046 381 131L381 21C381 9.95427 372.046 0.999968 361 0.999967L-9 0.999938"
      stroke="url(#glowTrail4)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeDasharray="150 750"
      strokeDashoffset="0"
      filter="url(#orangeGlow4)"
    >
      <animate
        attributeName="stroke-dashoffset"
        from="900"
        to="-900"
        dur="3s"
        repeatCount="indefinite"
      />
    </path>
  </svg> 
              </div> */}

        {/* 🔥 RIGHT SIDE TOP LINE */}
        {/* <div className="hidden lg:flex absolute top-72 right-6 w-[800px] h-[150px] justify-center items-center z-0 pointer-events-none right-top-line-adjust">
              <svg
    viewBox="0 0 735 152"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-full h-full object-contain"
    style={{ transform: 'scaleY(-1)', transformOrigin: '50% 10%' }}
  >
    <defs>
    
      <linearGradient
        id="baseLine3"
        x1="27"
        y1="76"
        x2="735"
        y2="76"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="white" stopOpacity="0.08" />
        <stop offset="1" stopColor="white" stopOpacity="0.0" />
      </linearGradient>

    
      <linearGradient id="glowTrail3" x1="0" y1="76" x2="250" y2="76" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="rgba(244, 76, 29, 0)" />
        <stop offset="40%" stopColor="rgba(244, 76, 29, 0.6)" />
        <stop offset="50%" stopColor="rgba(244, 76, 29, 1)" />
        <stop offset="60%" stopColor="rgba(244, 76, 29, 0.6)" />
        <stop offset="100%" stopColor="rgba(244, 76, 29, 0)" />
        <animateTransform
          attributeName="gradientTransform"
          type="translate"
          values="-709 0; 625 0"
          dur="3s"
          repeatCount="indefinite"
        />
      </linearGradient>

      <filter id="orangeGlow3" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="1" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="blur" />
          <feMergeNode in="blur" />
        </feMerge>
      </filter>
  
    </defs>


    <path
      d="M735 151L401 151C389.954 151 381 142.046 381 131L381 21C381 9.95427 372.046 0.999968 361 0.999967L-9 0.999938"
      stroke="url(#baseLine3)"
      strokeWidth="2"
      fill="none"
    />

    <path
      d="M735 151L401 151C389.954 151 381 142.046 381 131L381 21C381 9.95427 372.046 0.999968 361 0.999967L-9 0.999938"
      stroke="url(#glowTrail3)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeDasharray="150 550"
      strokeDashoffset="0"
      filter="url(#orangeGlow3)"
    >
      <animate
        attributeName="stroke-dashoffset"
        from="-700"
        to="700"
        dur="3s"
        repeatCount="indefinite"
      />
    </path>
  </svg>
  </div> */}
        {/* 🔥 LEFT SIDE BOTTOM LINE */}
        {/* <div className="hidden lg:flex absolute bottom-[40px] left-[10px] w-[600px] h-[150px] justify-center items-center z-0 pointer-events-none left-bottom-line-adjust">
              <svg
    viewBox="0 0 1100 150"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-full h-full object-contain"
    style={{ transform: 'scaleX(-1)', transformOrigin: '50% 50%' }}
  >
    <defs>

      <linearGradient
        id="baseLineBottomLeft"
        x1="27"
        y1="75"
        x2="1100"
        y2="75"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="white" stopOpacity="0.08" />
        <stop offset="1" stopColor="white" stopOpacity="0.01" />
      </linearGradient>

      
      <linearGradient id="glowTrailBottomLeft" x1="0" y1="75" x2="250" y2="75" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="rgba(244, 76, 29, 0)" />
        <stop offset="40%" stopColor="rgba(244, 76, 29, 0.6)" />
        <stop offset="50%" stopColor="rgba(244, 76, 29, 1)" />
        <stop offset="60%" stopColor="rgba(244, 76, 29, 0.6)" />
        <stop offset="100%" stopColor="rgba(244, 76, 29, 0)" />
        <animateTransform
          attributeName="gradientTransform"
          type="translate"
          values="-850 0; 1100 0"
          dur="3s"
          repeatCount="indefinite"
        />
      </linearGradient>

      <filter id="orangeGlowBottomLeft" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="1" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="blur" />
          <feMergeNode in="blur" />
        </feMerge>
      </filter>
    </defs>

    <path
      d="M1100 149L401 149C389.954 149 381 142.046 381 131L381 21C381 9.95427 372.046 0.999968 361 0.999967L-9 0.999938"
      stroke="url(#baseLineBottomLeft)"
      strokeWidth="3.5"
      fill="none"
    />


    <path
      d="M1100 149L401 149C389.954 149 381 142.046 381 131L381 21C381 9.95427 372.046 0.999968 361 0.999967L-9 0.999938"
      stroke="url(#glowTrailBottomLeft)"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeDasharray="150 950"
      strokeDashoffset="0"
      filter="url(#orangeGlowBottomLeft)"
    >
      <animate
        attributeName="stroke-dashoffset"
        from="-850"
        to="1100"
        dur="3s"
        repeatCount="indefinite"
      />
    </path>
  </svg>
              </div> */}

        {/* 🔥 RIGHT SIDE BOTTOM LINE */}
        {/* <div className="hidden lg:flex absolute top-3/4 -right-6 w-[900px] h-[150px] justify-center items-center z-0 pointer-events-none right-bottom-line-adjust">
          <svg
            viewBox="0 0 735 152"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-full object-contain"
          >
            <defs>

              <linearGradient
                id="baseLineBottomRight"
                x1="27"
                y1="76"
                x2="735"
                y2="76"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="white" stopOpacity="0.08" />
                <stop offset="1" stopColor="white" stopOpacity="0.01" />
              </linearGradient>

              <linearGradient id="glowTrailBottomRight" x1="0" y1="76" x2="250" y2="76" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="rgba(244, 76, 29, 0)" />
                <stop offset="40%" stopColor="rgba(244, 76, 29, 0.6)" />
                <stop offset="50%" stopColor="rgba(244, 76, 29, 1)" />
                <stop offset="60%" stopColor="rgba(244, 76, 29, 0.6)" />
                <stop offset="100%" stopColor="rgba(244, 76, 29, 0)" />
                <animateTransform
                  attributeName="gradientTransform"
                  type="translate"
                  values="-709 0; 625 0"
                  dur="3s"
                  repeatCount="indefinite"
                />
              </linearGradient>

              <filter id="orangeGlowBottomRight" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="1" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="blur" />
                  <feMergeNode in="blur" />
                </feMerge>
              </filter>
            </defs>

            <path
              d="M735 151L401 151C389.954 151 381 142.046 381 131L381 21C381 9.95427 372.046 0.999968 361 0.999967L-9 0.999938"
              stroke="url(#baseLineBottomRight)"
              strokeWidth="2"
              fill="none"
            />

      
            <path
              d="M735 151L401 151C389.954 151 381 142.046 381 131L381 21C381 9.95427 372.046 0.999968 361 0.999967L-9 0.999938"
              stroke="url(#glowTrailBottomRight)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="150 550"
              strokeDashoffset="0"
              filter="url(#orangeGlowBottomRight)"
            >
              <animate
                attributeName="stroke-dashoffset"
                from="-700"
                to="700"
                dur="3s"
                repeatCount="indefinite"
              />
            </path>
          </svg>
        </div> */}

        {/* ⭐ CARD CONTAINER */}
        <div className="relative z-20" style={{ overflow: 'visible', width: '100%' }}>
          <div ref={containerRef}>
            <Marquee
              speed={100}
              gradient={false}
              pauseOnHover={true}
              className="events-marquee"
            >
              {cards.map((card, index) => (
                <EventCard
                  key={card.id}
                  card={card}
                  index={index}
                  isCenter={centerCardId === card.id}
                />
              ))}
            </Marquee>
          </div>
        </div>
      </section>
    );
  };

  export default Events;
